import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api, { getErrorMessage } from '../lib/api';
import { useToast } from '../components/ui/Toast';
import { useElapsedTimer } from './useElapsedTimer';

/**
 * Shared generation flow for the image/music/code studios.
 *   const { generate, loading, elapsed, result, reset } = useGenerate('/generate/image');
 * Handles the elapsed timer, success cache invalidation, and graceful
 * error UX for rate limits (429), warming models (503), and plan limits (402).
 */
export function useGenerate(endpoint) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { elapsed, running, start, stop, reset: resetTimer } = useElapsedTimer();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const lastPayload = useRef(null);

  const generate = useCallback(
    async (payload) => {
      lastPayload.current = payload;
      setLoading(true);
      setResult(null);
      start();
      try {
        const { data } = await api.post(endpoint, payload);
        setResult(data.generation);
        qc.invalidateQueries({ queryKey: ['stats'] });
        qc.invalidateQueries({ queryKey: ['history'] });
        qc.invalidateQueries({ queryKey: ['usage'] });
        return data.generation;
      } catch (err) {
        const status = err?.response?.status;
        const code = err?.response?.data?.error?.code;

        if (status === 429 || code === 'MODEL_LOADING' || status === 503) {
          toast.rateLimit(() => generate(lastPayload.current));
        } else if (status === 402 || code === 'USAGE_LIMIT') {
          toast.error(getErrorMessage(err), {
            title: 'Plan limit reached',
            duration: Infinity,
            action: { label: 'Upgrade', onClick: () => navigate('/app/pricing') },
          });
        } else {
          toast.error(getErrorMessage(err, 'Generation failed.'), {
            action: { label: 'Retry', onClick: () => generate(lastPayload.current) },
          });
        }
        return null;
      } finally {
        setLoading(false);
        stop();
      }
    },
    [endpoint, qc, toast, navigate, start, stop]
  );

  const reset = useCallback(() => {
    setResult(null);
    resetTimer();
  }, [resetTimer]);

  return { generate, loading, elapsed, running, result, reset, setResult };
}
